import { useEffect, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import { Shirt } from "../../entities/Shirt";
import ShirtChart from "../../components/Chart/ShirtChart";
import ChartByTime from "../../components/Chart/ChartByTime";
import FormatService from "../../services/formatService";
import ShirtsService from "../../services/shirts/shirtsService";
import StatsService from "../../services/statsService";
import {
  FinancialElement,
  FinancialItemElement,
} from "../../services/shirts/types";
import { Dictionary } from "../../types/types";

interface FinancialStatsProps {
  userId: number;
}

const FinancialStats = ({ userId }: FinancialStatsProps) => {
  const [stats, setStats] = useState<Array<FinancialElement> | undefined>(
    undefined
  );
  const [valueDevShirtStats, setValueDevShirtStats] = useState<
    Array<FinancialItemElement>
  >([]);
  const [profitDevShirtStats, setProfitDevShirtStats] = useState<
    Array<FinancialItemElement>
  >([]);
  const [shirts, setShirts] = useState<Array<Shirt>>([]);
  const [collectionDates, setCollectionDates] = useState<
    Array<Date> | undefined
  >(undefined);
  const [valueDevShirt, setValueDevShirt] = useState<Shirt | null>(null);
  const [profitDevShirt, setProfitDevShirt] = useState<Shirt | null>(null);

  const { getFinancialStats, getItemFinancialStats } = StatsService();
  const { searchShirts } = ShirtsService();
  const { getMoneyFormat } = FormatService();

  const shirtCollectionSearch = () => {
    let params: Dictionary<string | number> = {};

    params["page"] = 1;
    params["per_page"] = 500;

    if (userId) {
      params["user_id"] = userId;
      searchShirts(params).then((results) => {
        setShirts(results.shirts);
      });
    }
  };

  useEffect(() => {
    if (userId) {
      shirtCollectionSearch();
      getFinancialStats(userId).then((results) => {
        if (results) {
          setStats(results);
        }
      });
    }
  }, [userId]);

  useEffect(() => {
    if (stats) {
      const processedDates = stats.map((element) => {
        return new Date(element.year, element.month - 1, 1);
      });
      setCollectionDates(processedDates);
    }
  }, [stats]);

  useEffect(() => {
    if (valueDevShirt !== null && valueDevShirt?.id) {
      getItemFinancialStats(valueDevShirt.id).then((result) => {
        if (result) {
          setValueDevShirtStats(result);
        }
      });
    }
  }, [valueDevShirt]);

  useEffect(() => {
    if (profitDevShirt !== null && profitDevShirt?.id) {
      getItemFinancialStats(profitDevShirt.id).then((result) => {
        if (result) {
          setProfitDevShirtStats(result);
        }
      });
    }
  }, [profitDevShirt]);

  const blockStyle = {
    maxWidth: "860px !important",
    width: "100%",
    paddingBottom: "20px",
    paddingTop: "20px",
    borderRadius: "10px",
    backgroundColor: "white !important",
    minHeight: "50px",
    paddingLeft: "16px",
    paddingRight: "16px",
  };

  return stats ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: 2,
        marginTop: "18px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...blockStyle,
        }}
      >
        {/* Value development shirt collection */}
        {collectionDates && (
          <ChartByTime
            xData={collectionDates}
            yData={stats.map((element) => parseFloat(element.total_price))}
            title="Value development shirt collection"
            withMonth={true}
            valueFormatter={getMoneyFormat}
            showButtons={true}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "18px",
          ...blockStyle,
        }}
      >
        {/* Profit development shirt collection */}
        {collectionDates && (
          <ChartByTime
            xData={collectionDates}
            yData={stats.map((element) => parseFloat(element.total_profit))}
            title="Profit development shirt collection"
            withMonth={true}
            valueFormatter={getMoneyFormat}
            showButtons={true}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "18px",
          ...blockStyle,
        }}
      >
        {/* Value development per shirt */}
        {valueDevShirtStats && (
          <ShirtChart
            options={shirts}
            selectedShirt={valueDevShirt}
            setSelectedShirt={setValueDevShirt}
            xData={valueDevShirtStats.map(
              (element) => new Date(element.year, element.month - 1, 1)
            )}
            yData={valueDevShirtStats.map((element) =>
              parseFloat(element.price)
            )}
            title="Value development per shirt"
            valueFormatter={getMoneyFormat}
            withMonth={true}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "18px",
          ...blockStyle,
        }}
      >
        <ShirtChart
          options={shirts}
          selectedShirt={profitDevShirt}
          setSelectedShirt={setProfitDevShirt}
          xData={profitDevShirtStats.map(
            (element) => new Date(element.year, element.month - 1, 1)
          )}
          yData={profitDevShirtStats.map((element) =>
            parseFloat(element.profit)
          )}
          title="Profit development per shirt"
          valueFormatter={getMoneyFormat}
          withMonth={true}
        />
      </Box>
    </Box>
  ) : (
    <Skeleton variant="rectangular" height={500} animation="wave" />
  );
};

export default FinancialStats;
